import React from "react";
import { Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  quote: "The curation process is impeccable. Every piece in the collection meets a standard of excellence we haven't found elsewhere in the industry.",
  author: "Marcus Thorne",
  role: "Director at Aura Logistics",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/avatar_sarah_jenkins.png"
}, {
  id: 2,
  quote: "Our operational efficiency improved significantly after integrating these solutions. The attention to detail and industrial precision is remarkable.",
  author: "Elena Vance",
  role: "COO at Vector Dynamics",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/avatar_michael_chen.png"
}, {
  id: 3,
  quote: "The support team is exceptionally responsive. They understand the nuances of our high-stakes requirements and provide tailored solutions.",
  author: "Julian Ross",
  role: "Head of Design at Pillar Group",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/avatar_elena_rodriguez.png"
}, {
  id: 4,
  quote: "A seamless experience from start to finish. The interface is intuitive, and the results speak for themselves. Truly a benchmark for B2B excellence.",
  author: "Sophia Chen",
  role: "Founder at Lumina Systems",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/avatar_david_park.png"
}, {
  id: 5,
  quote: "Starting with just 50 pieces allowed us to test our brand concept without a massive upfront investment. The customization options are top-tier.",
  author: "Oliver Bennett",
  role: "Founder of Nexus Timepieces",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/avatar_oliver_bennett.png"
}, {
  id: 6,
  quote: "The turnaround time for our private label collection was significantly faster than expected. It's the perfect partner for any emerging watch brand.",
  author: "Amelia Wright",
  role: "Product Lead at Vera Watches",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/avatar_amelia_wright.png"
}];
const logos = [{
  name: "Aura Logistics",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/logo_acme_corp.png"
}, {
  name: "Vector Dynamics",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/logo_quantum_systems.png"
}, {
  name: "Pillar Group",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/logo_echo_valley.png"
}, {
  name: "Lumina Systems",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/logo_nebula_tech.png"
}, {
  name: "Apex Solutions",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/logo_global_solutions.png"
}, {
  name: "Zenith Corp",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/logo_apex_innovations.png"
}, {
  name: "Core Industries",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/logo_horizon_group.png"
}];
export default function TestimonialsClientLogos() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="21757" id="trusted-by-brands" ref={ref} className={`py-24 bg-muted transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-5xl font-sans font-bold tracking-tight mb-4 text-foreground">
            Why Work With Us
          </h2>
          <ul className="text-secondary max-w-2xl mx-auto text-lg font-sans space-y-2 list-none">
            <li>10+ years manufacturing experience</li>
            <li>Direct factory (no middleman)</li>
            <li>Stable quality control</li>
          </ul>
        </div>

        <div className="flex justify-center mb-24">
          <Carousel opts={{
          align: "center",
          loop: true
        }} orientation="vertical" className="w-full max-w-4xl relative [&_[data-slot=carousel-content]]:h-[400px] md:[&_[data-slot=carousel-content]]:h-[350px]">
            <CarouselContent>
              {testimonials.map((t, i) => <CarouselItem key={t.id} className="basis-full pt-4" data-index={i}>
                  <div className="h-full flex flex-col justify-center items-center text-center p-8 md:p-10 bg-card rounded-lg border-l-4 border-l-primary shadow-sm">
                    <Quote className="size-10 text-primary/10 mb-6 fill-current" />
                    <blockquote className="text-xl md:text-2xl font-sans font-medium mb-8 leading-relaxed text-foreground max-w-3xl">
                      &ldquo;{t.quote}&rdquo;
                    </blockquote>
                    <div className="flex items-center gap-4">
                      <img src={t.avatar} alt={t.author} className="size-12 rounded-full object-cover border border-border" />
                      <div className="text-left font-sans">
                        <div className="font-semibold text-foreground">
                          {t.author}
                        </div>
                        <div className="text-sm text-secondary">
                          {t.role}
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="-top-12 left-1/2 -translate-x-1/2 rotate-90 border-border hover:bg-card/50" />
              <CarouselNext className="-bottom-12 left-1/2 -translate-x-1/2 rotate-90 border-border hover:bg-card/50" />
            </div>
          </Carousel>
        </div>

        <div className="border-t border-border pt-16">
          <p className="text-center text-sm font-sans font-semibold text-secondary uppercase tracking-widest mb-10">
            Powering next-gen infrastructure
          </p>
          
          <div className="relative px-8 md:px-12">
            <Carousel opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent className="-ml-8 items-center">
                {logos.map((logo, i) => <CarouselItem key={i} className="pl-8 basis-1/2 md:basis-1/4 lg:basis-1/5" data-index={i}>
                    <div className="flex justify-center items-center h-20 grayscale hover:grayscale-0 transition-all duration-500 opacity-40 hover:opacity-100 cursor-pointer">
                      <img src={logo.src} alt={logo.name} className="h-8 w-auto object-contain max-w-[140px]" />
                    </div>
                  </CarouselItem>)}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-4 border-border hover:bg-card/50" />
              <CarouselNext className="hidden md:flex -right-4 border-border hover:bg-card/50" />
            </Carousel>
          </div>
        </div>
      </div>
    </section>;
}